import React from 'react';
import { GenericDialogue } from 'src/components/Dialogue/Dialogue';
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import SchedulingBodySkeleton from './SchedulingBodySkeleton';
import PreferredTimesSummary from '../components/PreferredTimesSummary/PreferredTimesSummary';
import ServiceRequestSummaryTable from '../components/ServiceRequestSummaryTable/ServiceRequestSummaryTable';
import ManualSchedulingPanel from '../components/ManualSchedulingPanel/ManualSchedulingPanel';
import {
  useServiceRequestSchedulingOrchestrator,
  UseServiceRequestSchedulingOrchestratorReturn,
} from '../CustomHooks/useServiceRequestSchedulingOrchestrator';
import { IServiceRequestDetail } from 'src/services/api/ServiceRequest/apiContracts';
import './service-request-scheduling.scss';

interface ServiceRequestSchedulingProps {
  serviceRequest: IServiceRequestDetail;
  onClose: () => void;
  onEventScheduled: (data: any) => void;
}

const ServiceRequestScheduling = ({
  serviceRequest,
  onClose,
  onEventScheduled,
}: ServiceRequestSchedulingProps) => {
  const {
    isLoading,
    scheduledAt,
    scheduledAtLabel,
    availableServiceTimes,
    isLoadingAvailability,
    isScheduling,
    hasPreferredTimes,
    preferredDays,
    preferredEntries,
    activePreferredDay,
    onPreferredDaySelect,
    showPreferenceMismatchModal,
    onConfirmScheduleAnyway,
    onCancelScheduleAnyway,
    propertyName,
    propertyAddress,
    suiteNumber,
    homeownerName,
    homeownerProfileImage,
    onScheduledAtChange,
    onScheduleClick,
  }: UseServiceRequestSchedulingOrchestratorReturn =
    useServiceRequestSchedulingOrchestrator({
      serviceRequest,
      onClose,
      onEventScheduled,
    });

  return (
    <GenericDialogue
      isOpen
      onClose={onClose}
      title="Schedule Service Request"
      maxWidth="135rem"
      minHeight="92vh"
    >
      <div className="service-request-scheduling">
        <ServiceRequestSummaryTable
          propertyName={propertyName}
          propertyAddress={propertyAddress}
          suiteNumber={suiteNumber}
          homeownerName={homeownerName}
          homeownerProfileImage={homeownerProfileImage}
        />
        {isLoading ? (
          <SchedulingBodySkeleton />
        ) : (
          <>
            <PreferredTimesSummary
              preferredDays={preferredDays}
              preferredEntries={preferredEntries}
              activePreferredDay={activePreferredDay}
              onPreferredDaySelect={onPreferredDaySelect}
            />
            <div className="preferred-appointment-times">
              <h3 className="scheduling-section-heading">
                Select a Time for the Appointment
              </h3>
              <div className="scheduling-calendar-box">
                <ManualSchedulingPanel
                  hasPreferredTimes={hasPreferredTimes}
                  availableServiceTimes={availableServiceTimes}
                  isLoadingAvailability={isLoadingAvailability}
                  isScheduling={isScheduling}
                  scheduledAt={scheduledAt}
                  onScheduledAtChange={onScheduledAtChange}
                  onScheduleClick={onScheduleClick}
                />
              </div>
            </div>
          </>
        )}
      </div>
      {showPreferenceMismatchModal && (
        <ConfirmationModal
          variant="warning"
          title="Outside the homeowner's preferred times"
          message={`You're scheduling for ${scheduledAtLabel}, which isn't one of the times the homeowner said works for them. Are you sure you want to schedule this?`}
          confirmLabel="Schedule Anyway"
          cancelLabel="Cancel"
          isSubmitting={isScheduling}
          onConfirm={() => void onConfirmScheduleAnyway()}
          onCancel={onCancelScheduleAnyway}
        >
          <div className="schedule-mismatch-preferred">
            <span className="mismatch-label">
              Homeowner's preferred times
            </span>
            <div className="mismatch-pills">
              {preferredEntries.map((entry: string) => (
                <CategoryPill
                  key={entry}
                  text={entry}
                  size="small"
                  color="var(--color-teal)"
                  textColor="white"
                  iconGlyph="clock"
                  iconWeight="regular"
                />
              ))}
            </div>
          </div>
        </ConfirmationModal>
      )}
    </GenericDialogue>
  );
};

export default ServiceRequestScheduling;
